import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { validateEmail, validatePhone, validateRequired, validateMinLength } from '../../utils/validation';
import emailjs from '@emailjs/browser';

// Initialisation d'EmailJS - Remplacez par votre clé publique
emailjs.init({
  publicKey: 'T1HEihzBKRrMt6WgJ',
});

// Configuration EmailJS - Remplacez par vos IDs
const SERVICE_ID = 'service_kip6hnk'; 
const TEMPLATE_ID = 'template_mead9i8';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  projectType: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

// URL de l'API backend
const API_URL = 'http://localhost:3001/api/send-email';

const ContactForm: React.FC = () => {
  const location = useLocation();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    projectType: 'construction'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const projectTypes = [
    { value: 'construction', label: 'Travaux neufs' },
    { value: 'renovation', label: 'Rénovation' },
    { value: 'maintenance', label: 'Réparation et maintenance' },
    { value: 'autre', label: 'Autre' }
  ];

  // Récupérer les paramètres d'URL et pré-remplir le formulaire
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const serviceTitle = params.get('service');
      const serviceType = params.get('type');
      const projectTitle = params.get('project');
      
      // Si un service est spécifié dans l'URL
      if (serviceTitle) {
        // Déterminer le type de projet en fonction de l'ID du service
        let projectType = 'autre';
        if (serviceType === '1') projectType = 'construction';
        if (serviceType === '2') projectType = 'renovation';
        if (serviceType === '3') projectType = 'maintenance';
        
        // Construire un message par défaut
        const defaultMessage = `Bonjour,\n\nJe souhaite obtenir un devis pour votre service "${serviceTitle}".\n\nMerci de me contacter pour discuter de mon projet.`;
        
        // Mettre à jour le formulaire
        setFormData(prev => ({
          ...prev,
          subject: `Demande de devis - ${serviceTitle}`,
          message: defaultMessage,
          projectType
        }));
      } else if (projectTitle) {
        // Déterminer le type de projet en fonction de la catégorie du projet
        let projectType = 'autre';
        if (serviceType === 'construction') projectType = 'construction';
        if (serviceType === 'renovation') projectType = 'renovation';
        if (serviceType === 'maintenance') projectType = 'maintenance';
        
        // Construire un message par défaut
        const defaultMessage = `Bonjour,\n\nJe souhaite obtenir un devis pour un projet similaire à "${projectTitle}".\n\nMerci de me contacter pour discuter de mon projet.`;
        
        // Mettre à jour le formulaire
        setFormData(prev => ({
          ...prev,
          subject: `Demande de devis - Projet similaire à ${projectTitle}`,
          message: defaultMessage,
          projectType
        }));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres d'URL:", error);
    }
  }, [location.search]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!validateRequired(formData.name)) {
      newErrors.name = 'Veuillez entrer votre nom';
    }
    
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Veuillez entrer votre email';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Veuillez entrer un email valide';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone valide';
    }
    
    if (!validateRequired(formData.subject)) {
      newErrors.subject = 'Veuillez entrer un sujet';
    }
    
    if (!validateRequired(formData.message)) {
      newErrors.message = 'Veuillez entrer votre message';
    } else if (!validateMinLength(formData.message, 20)) {
      newErrors.message = 'Votre message doit contenir au moins 20 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Préparation des paramètres pour l'envoi d'email
      const emailData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        projectType: projectTypes.find(type => type.value === formData.projectType)?.label || formData.projectType
      };
      
      // Utiliser EmailJS pour envoyer l'email
      try {
        // Préparation des paramètres pour l'email avec reply_to
        const emailParams = {
          ...emailData,
          reply_to: formData.email,
          type: projectTypes.find(type => type.value === formData.projectType)?.label || formData.projectType
        };
        
        // Envoi de l'email via EmailJS
        const result = await emailjs.send(
          SERVICE_ID,
          TEMPLATE_ID,
          emailParams
        );
        
        if (result.status === 200) {
          console.log('Email envoyé avec succès via EmailJS');
          // Success response
          setSubmitStatus('success');
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            projectType: 'construction'
          });
          return;
        }
      } catch (emailjsError) {
        console.error('Erreur EmailJS, tentative avec l\'API de secours:', emailjsError);
      }
      
      // Si EmailJS échoue, utiliser l'API de secours
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Success response
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          projectType: 'construction'
        });
      } else {
        // Error response
        console.error('Erreur lors de l\'envoi de l\'email via l\'API:', data.message);
        setSubmitStatus('error');
      }
    } catch (error) {
      // Error response
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 w-full">
      {submitStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-3 sm:p-4 bg-green-100 text-green-800 rounded-md flex items-start sm:items-center flex-wrap sm:flex-nowrap"
        >
          <FontAwesomeIcon icon={faCheck} className="mr-3 text-green-500 mt-1 sm:mt-0" />
          <span className="text-sm sm:text-base">Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.</span>
        </motion.div>
      )}
      
      {submitStatus === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-3 sm:p-4 bg-red-100 text-red-800 rounded-md flex items-start sm:items-center flex-wrap sm:flex-nowrap"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500 mt-1 sm:mt-0" />
          <span className="text-sm sm:text-base">Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer ultérieurement.</span>
        </motion.div>
      )}
      
      <form onSubmit={handleSubmit} className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Nom */}
          <div className="col-span-1">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nom complet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 sm:px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Votre nom"
            />
            {errors.name && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.name}</p>
            )}
          </div>
          
          {/* Email */}
          <div className="col-span-1">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 sm:px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Votre email"
            />
            {errors.email && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.email}</p>
            )}
          </div>
          
          {/* Téléphone */}
          <div className="col-span-1">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-3 sm:px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Votre numéro de téléphone"
            />
            {errors.phone && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.phone}</p>
            )}
          </div>
          
          {/* Type de projet */}
          <div className="col-span-1">
            <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1">
              Type de projet
            </label>
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {projectTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Sujet */}
          <div className="col-span-1 sm:col-span-2">
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Sujet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full px-3 sm:px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.subject ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Sujet de votre message"
            />
            {errors.subject && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.subject}</p>
            )}
          </div>
          
          {/* Message */}
          <div className="col-span-1 sm:col-span-2">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className={`w-full px-3 sm:px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Votre message"
            ></textarea>
            {errors.message && (
              <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.message}</p>
            )}
          </div>
        </div>
        
        <div className="mt-6 sm:mt-8">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-6 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors duration-300 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
